// Singleton loader for the Google Maps JS API + Places library.
//
// Anything that needs Google Maps / Places (AddressAutocomplete component,
// distance utilities, etc.) calls loadGoogleMaps() — first call kicks off
// the script load, subsequent calls share the in-flight promise and
// resolve as soon as it lands.
//
// The API key comes from REACT_APP_GOOGLE_MAPS_API_KEY (configure in .env
// or .env.local). When the key is missing the loader resolves with
// `{ available: false }` and callers must degrade gracefully (e.g.
// AddressAutocomplete falls back to a plain <input>). No crash, no console
// noise beyond a single warn so devs know what to fix.

let cached = null;

export function loadGoogleMaps() {
  if (cached) return cached;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    // eslint-disable-next-line no-console
    console.warn(
      "[googleMaps] REACT_APP_GOOGLE_MAPS_API_KEY is not set — AddressAutocomplete will fall back to a plain text input."
    );
    cached = Promise.resolve({ available: false });
    return cached;
  }

  // If something already loaded the script (other code, hot reload), reuse it.
  if (typeof window !== "undefined" && window.google?.maps?.places) {
    cached = Promise.resolve({ available: true, google: window.google });
    return cached;
  }

  cached = new Promise((resolve) => {
    // Avoid double-injecting the script if the user mounts two
    // AddressAutocomplete instances near-simultaneously.
    const existing = document.querySelector("script[data-google-maps-loader]");
    const onReady = async () => {
      try {
        // With loading=async the Places library isn't guaranteed to be on
        // window.google at script onload; importLibrary("places") resolves only
        // once it's actually ready. (No-op when places is already present, so
        // existing callers are unaffected.)
        if (window.google?.maps?.importLibrary && !window.google.maps.places) {
          await window.google.maps.importLibrary("places");
        }
      } catch (e) {
        /* fall through to the availability check below */
      }
      if (window.google?.maps?.places) {
        resolve({ available: true, google: window.google });
      } else {
        // eslint-disable-next-line no-console
        console.warn("[googleMaps] script loaded but `google.maps.places` is missing — falling back.");
        resolve({ available: false });
      }
    };

    if (existing) {
      existing.addEventListener("load", onReady, { once: true });
      existing.addEventListener("error", () => resolve({ available: false }), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&loading=async&v=weekly`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "true";
    script.onload = onReady;
    script.onerror = () => {
      // eslint-disable-next-line no-console
      console.warn("[googleMaps] script failed to load — falling back.");
      resolve({ available: false });
    };
    document.head.appendChild(script);
  });

  return cached;
}

// Convert a google.maps.places.PlaceResult into a flat address shape the
// rest of the app already speaks (matches the field names used in
// auth.orgInfo / order.shipping / clinical-profile address blocks).
export function parsePlace(place) {
  if (!place || !Array.isArray(place.address_components)) return null;
  const get = (type, opt = "long_name") => {
    const c = place.address_components.find((cc) => cc.types.includes(type));
    return c ? c[opt] : "";
  };
  const streetNumber = get("street_number");
  const route = get("route");
  return {
    address: [streetNumber, route].filter(Boolean).join(" "),
    address2: get("subpremise"),
    city: get("locality") || get("sublocality_level_1") || get("postal_town"),
    // Use short_name for state/country so callers get "FL" / "US", which
    // match the existing two-letter conventions in shipping payloads.
    state: get("administrative_area_level_1", "short_name"),
    zip: get("postal_code"),
    country: get("country", "short_name"),
    formatted: place.formatted_address || "",
    lat: place.geometry?.location?.lat?.() ?? null,
    lng: place.geometry?.location?.lng?.() ?? null,
  };
}
